import { useState, useEffect } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// @mui material components
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAvatar from 'components/MDAvatar';
import MDBadge from 'components/MDBadge';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';

// Material Dashboard 2 PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import bgProfile from 'assets/images/bg-sign-in-cover.jpeg';

// Components
import ProductionResponsibles from 'layouts/production/Components/ProductionResponsibles';
import ProductionTags from 'layouts/production/Components/ProductionTags';
import ProductionAndProjectRelation from 'layouts/production/Components/ProductionAndProjectRelation';

// Helper functions
import { getDaysPassedColor } from 'layouts/production/helpers/dateHelpers';
import { updateProduction, deleteProduction } from 'services/api/production';
import { notify } from 'helpers/notificationHelper';
import { config } from 'Constants';

const { PRODUCTION_GENERAL_TAGS_GROUP_ID } = config.fixedID;

const getStatusBadgeProps = (isActive, aaroStatus) => {
    if (!isActive) {
        return { color: 'error', variant: 'gradient' };
    }
    if (aaroStatus) {
        return { color: 'success', variant: 'gradient' };
    }
    return { color: 'warning', variant: 'gradient' };
};

const getStatusDisplayText = (isActive, aaroStatus) => {
    if (!isActive) return 'Pasif';
    if (aaroStatus) return 'Aktif';
    return 'Beklemede';
};

function ProductionLayout({ children, setValue, value, isDialog = false, production }) {
    const [tabValue, setTabValue] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [projectDialogOpen, setProjectDialogOpen] = useState(false);
    const open = Boolean(anchorEl);

    useEffect(() => {
        setTabValue(value);
    }, [value]);

    const handleSetTabValue = (event, newValue) => {
        setTabValue(newValue);
        setValue(newValue);
    };

    const handleSettingsClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setAnchorEl(null);
    };

    const handleTagDialogOpen = () => {
        setTagDialogOpen(true);
        handleSettingsClose();
    };

    const handleTagDialogClose = () => {
        setTagDialogOpen(false);
    };

    const handleProjectDialogOpen = () => {
        setProjectDialogOpen(true);
        handleSettingsClose();
    };

    const handleProjectDialogClose = () => {
        setProjectDialogOpen(false);
    };

    const handleDeactivateProduction = () => {
        // TODO: Implement deactivate production logic
        updateProduction(production?._id, { isActive: !production?.isActive })
            .then((rp) => {
                notify(true, `Üretim ${production?.isActive ? 'pasife' : 'aktive'} edildi`);
            })
            .catch((err) => {
                console.log(err);
            });
        handleSettingsClose();
    };

    const handleDeleteProduction = () => {
        deleteProduction(production?._id)
            .then((rp) => {
                notify(true, 'Üretim başarıyla silindi');
            })
            .catch((err) => {
                console.log(err);
            });
        handleSettingsClose();
    };

    const daysOrderCreated = Math.floor(
        (new Date() - new Date(production?.aaroFields?.OlsTar)) / (1000 * 60 * 60 * 24)
    );
    console.log(production);

    const tabs = [
        { label: 'Ana Sayfa', icon: <HomeIcon /> },
        { label: 'Aaro Siparişi', icon: <ReceiptIcon /> },
        { label: 'Aaro Sözleşmesi', icon: <DescriptionIcon /> },
        { label: 'Fiyat Çalışması', icon: <FolderIcon /> },
        { label: 'E-Postalar', icon: <EmailIcon /> },
    ];

    // Dialog modunda DashboardLayout kullanmıyoruz
    const content = (
        <MDBox>
            {/* Hero Section */}
            <MDBox
                sx={{
                    backgroundImage: `url(${bgProfile})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: isDialog ? 0 : 3,
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '150px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)',
                    },
                }}
            ></MDBox>

            {/* Profile Card */}
            <Card
                sx={{
                    marginTop: '-80px',
                    marginX: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <MDBox p={3}>
                    <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} md={8}>
                            <MDBox display="flex" alignItems="center" gap={3}>
                                {/* Production Avatar */}
                                <MDAvatar
                                    sx={{
                                        width: 80,
                                        height: 80,

                                        fontSize: '2rem',
                                        fontWeight: 'bold',
                                    }}
                                    bgColor={getDaysPassedColor(production?.aaroFields, daysOrderCreated)}
                                >
                                    {/* buraya sipariş alınanı kaç gün yazdığı gelsin ve avatar rengi de ona göre belirlensin. production.aaroFiels.OlsTar ile gösterilecek */}
                                    <MDTypography variant="h3" fontWeight="bold" color="white">
                                        {daysOrderCreated}
                                    </MDTypography>
                                </MDAvatar>

                                {/* Production Info */}
                                <MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <MDTypography variant="h6" fontWeight="bold" color="dark">
                                            {production?.productionName || 'Üretim Adı'}
                                        </MDTypography>

                                        {/* Tags as hashtags */}
                                        {production?.tags && production.tags.length > 0 && (
                                            <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                {production.tags.map((tag) => (
                                                    <MDTypography
                                                        key={tag._id}
                                                        variant="overline"
                                                        sx={{
                                                            color: tag.color || '#1976d2',
                                                            opacity: 1,
                                                        }}
                                                    >
                                                        #{tag.name?.toLocaleUpperCase()}
                                                    </MDTypography>
                                                ))}
                                            </MDBox>
                                        )}
                                    </MDBox>

                                    <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={1}>
                                        <MDTypography variant="caption" color="text">
                                            {production?.aaroFields?.OlsAdi || '-'} tarafından oluşturuldu
                                        </MDTypography>

                                        <MDTypography variant="caption" color="bold">
                                            #{production?.aaroFields?.BelgeNo}
                                        </MDTypography>

                                        <ProductionResponsibles
                                            production={production}
                                            avatarSize="xs"
                                            showAddButton={true}
                                            additionalStyle={{
                                                border: 'none',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                ml: 0,
                                                mr: 0.5,
                                                '&:hover, &:focus': {
                                                    zIndex: '10',
                                                    transform: 'scale(1.1)',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <MDBox display="flex" alignItems="center" justifyContent="flex-end">
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={handleSettingsClick}
                                >
                                    Ayarlar
                                </MDButton>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleSettingsClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    PaperProps={{
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                                                fontSize: '0.875rem',
                                                fontWeight: 400,
                                                '& .MuiListItemText-primary': {
                                                    fontFamily:
                                                        '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 400,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem onClick={handleTagDialogOpen}>
                                        <ListItemIcon>
                                            <FolderIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Etiket Ekle</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={handleProjectDialogOpen}>
                                        <ListItemIcon>
                                            <LinkIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Proje İlişkilendir</ListItemText>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleDeactivateProduction}>
                                        <ListItemIcon>
                                            {production?.isActive ? (
                                                <PauseIcon fontSize="small" />
                                            ) : (
                                                <PlayArrowIcon fontSize="small" />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText>
                                            {production?.isActive ? 'Üretimi Pasife Al' : 'Üretimi Aktive Et'}
                                        </ListItemText>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleDeleteProduction} disabled>
                                        <ListItemIcon>
                                            <DeleteIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Sil</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>
            </Card>

            {/* Navigation Tabs */}
            <MDBox mt={3} mx={3}>
                <Card>
                    <MDBox px={3} py={1}>
                        <Tabs
                            value={tabValue}
                            onChange={handleSetTabValue}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    minHeight: '60px',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 'medium',
                                    color: 'text.secondary',
                                    '&.Mui-selected': {
                                        color: 'info.main',
                                        fontWeight: 'bold',
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'info.main',
                                    height: '3px',
                                    borderRadius: '3px',
                                },
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    label={tab.label}
                                    icon={tab.icon}
                                    iconPosition="start"
                                    sx={{
                                        '& .MuiTab-iconWrapper': {
                                            marginRight: 1,
                                            marginBottom: 0,
                                        },
                                    }}
                                />
                            ))}
                        </Tabs>
                    </MDBox>
                </Card>
            </MDBox>

            {/* Content Area */}
            <MDBox mt={3} mx={3}>
                {children}
            </MDBox>

            {/* Tag Dialog */}
            {production && PRODUCTION_GENERAL_TAGS_GROUP_ID && tagDialogOpen && (
                <ProductionTags
                    production={production}
                    tagLabel={'Etiketler'}
                    tagGroupID={PRODUCTION_GENERAL_TAGS_GROUP_ID}
                    multiple={true}
                    dbKey={'tags'}
                    open={tagDialogOpen}
                    onClose={handleTagDialogClose}
                />
            )}

            {/* Project Association Dialog */}
            {production && projectDialogOpen && (
                <ProductionAndProjectRelation
                    production={production}
                    setValue={setValue}
                    open={projectDialogOpen}
                    onClose={handleProjectDialogClose}
                />
            )}
        </MDBox>
    );

    // Dialog modunda DashboardLayout ve Footer kullanmıyoruz
    if (isDialog) {
        return content;
    }

    return (
        <DashboardLayout>
            {content}
            <Footer />
        </DashboardLayout>
    );
}

// Setting default values for the props of ProductionLayout
ProductionLayout.defaultProps = {
    stickyNavbar: false,
};

// Typechecking props for ProductionLayout
ProductionLayout.propTypes = {
    children: PropTypes.node.isRequired,
    setValue: PropTypes.func.isRequired,
    value: PropTypes.number.isRequired,
    isDialog: PropTypes.bool,
    production: PropTypes.object,
};

export default ProductionLayout;
